<template>
  <v-container fluid class="pa-0">
    <el-overlay-loading :loading="pageloading" />
    <h1 class="mb-1">{{ $t(getTitle) }}</h1>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" class="pb-0">
            <el-text-field
              :label="$t('Kodas')"
              v-model="setForm.code"
              :maxlength="100"
              required
            />
          </v-col>
          <v-col cols="12" md="8" class="pb-0">
            <el-text-field
              :label="$t('Pavadinimas')"
              v-model="setForm.title"
              :maxlength="500"
              required
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <el-textarea
              :label="$t('Aprašymas')"
              v-model="setForm.description"
              :maxlength="2000"
              required
              :rows="4"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <el-button
          title="Atšaukti"
          color="secondary"
          icon="atšaukti"
          @click.native.stop="back()"
          :loading="loading"
        />
        <el-button
          title="Išsaugoti"
          color="primary"
          icon="išsaugoti"
          @click.native.stop="saveSet()"
          :loading="loading"
        />
      </v-card-actions>
    </v-card>

    <template v-if="setId">
      <v-card outlined class="mb-4">
        <v-tabs v-model="activeTab" background-color="transparent">
          <v-tab>{{ $t('Laukai ir taisyklės') }}</v-tab>
          <v-tab>{{ $t('Priskirtos paslaugos') }}</v-tab>
        </v-tabs>
        <v-card-text>
          <v-tabs-items v-model="activeTab" class="transparent">
            <v-tab-item>
              <div class="mb-6">
            <div class="d-flex align-center subtitle-1 mb-2">
              <h4 class="mb-1">{{ $t('Papildomi laukai') }}</h4>
              <v-spacer />
              <el-button
                color="primary"
                icon="pridėti"
                title="Pridėti lauką"
                :loading="fieldsLoading"
                @click.native.stop="openFieldModal(null)"
              />
            </div>
            <v-data-table
              class="custom-table"
              :headers="fieldHeaders"
              :items="fieldsList"
              :loading="fieldsLoading"
              :hide-default-footer="fieldsList.length <= 10"
              disable-pagination
            >
              <template #[`item.actions`]="{ item }">
                <div class="nowrap">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="mr-2 table-action"
                        v-bind="attrs"
                        v-on="on"
                        @click="openFieldModal(item)"
                      >
                        mdi-pencil-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('Redaguoti') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="table-action"
                        v-bind="attrs"
                        v-on="on"
                        @click="deleteField(item)"
                      >
                        mdi-delete-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('Šalinti') }}</span>
                  </v-tooltip>
                </div>
              </template>
              <template #[`item.field_type`]="{ item }">
                {{ formatActionExtraFieldType(item.field_type) }}
              </template>
              <template #[`item.manual_input_type`]="{ item }">
                {{ formatActionExtraFieldManualInputType(item) }}
              </template>
              <template slot="no-data">{{ $t('Duomenų nėra') }}</template>
            </v-data-table>
          </div>

          <div class="m-6">
            <div class="d-flex align-center subtitle-1 mb-2">
              <h4 class="mb-1">{{ $t('Taisyklės') }}</h4>
              <v-spacer />
              <el-button
                color="primary"
                icon="pridėti"
                title="Pridėti taisyklę"
                :loading="rulesLoading"
                :disabled="!fieldsList.length"
                @click.native.stop="openRuleModal(null)"
              />
            </div>
            <v-data-table
              class="custom-table"
              :headers="ruleHeaders"
              :items="rulesList"
              :loading="rulesLoading"
              :hide-default-footer="rulesList.length <= 10"
              disable-pagination
            >
              <template #[`item.fields_summary`]="{ item }">
                {{ formatRuleFields(item) }}
              </template>
              <template #[`item.required_summary`]="{ item }">
                {{ item.required_count }} / {{ (item.field_ids || []).length }}
              </template>
              <template #[`item.actions`]="{ item }">
                <div class="nowrap">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="mr-2 table-action"
                        v-bind="attrs"
                        v-on="on"
                        @click="openRuleModal(item)"
                      >
                        mdi-pencil-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('Redaguoti') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="table-action"
                        v-bind="attrs"
                        v-on="on"
                        @click="deleteRule(item)"
                      >
                        mdi-delete-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('Šalinti') }}</span>
                  </v-tooltip>
                </div>
              </template>
              <template slot="no-data">{{ $t('Duomenų nėra') }}</template>
            </v-data-table>
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="mb-6 mt-4">
                <div class="d-flex align-center subtitle-1 mb-2">
                  <h4 class="mb-1">{{ $t('Paslaugos ir veiksmai') }}</h4>
                </div>
                <v-data-table
                  class="custom-table"
                  :headers="actionHeaders"
                  :items="actionsList"
                  :loading="actionsLoading"
                  :hide-default-footer="actionsList.length <= 10"
                  disable-pagination
                >
                  <template slot="no-data">{{ $t('Duomenų nėra') }}</template>
                </v-data-table>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </template>

    <modal-action-extra-field
      v-model="showFieldModal"
      :set-id="setId"
      :field="fieldEdit"
      @saved="refreshTablesFromShowEndpoint"
    />
    <modal-action-extra-field-rule
      v-model="showRuleModal"
      :set-id="setId"
      :fields="fieldsList"
      :rule="ruleEdit"
      @saved="refreshTablesFromShowEndpoint"
    />
    <modal-delete
      title="Šalinti įrašą"
      text="Ar tikrai norite pašalinti įrašą?"
      v-model="showDeleteField"
      :loading="deleteLoading"
      :item="deleteFieldItem"
      @confirmDelete="confirmDeleteField"
    />
    <modal-delete
      title="Šalinti įrašą"
      text="Ar tikrai norite pašalinti įrašą?"
      v-model="showDeleteRule"
      :loading="deleteLoading"
      :item="deleteRuleItem"
      @confirmDelete="confirmDeleteRule"
    />
  </v-container>
</template>

<script>
import userRole from '@/mixins/userRole'
import Enums from '@/enums'
import ModalDelete from '@/components/modals/ModalDelete'
import ModalActionExtraField from './ModalActionExtraField.vue'
import ModalActionExtraFieldRule from './ModalActionExtraFieldRule.vue'

export default {
  name: 'AdminActionExtraFieldSetForm',
  components: {
    ModalDelete,
    ModalActionExtraField,
    ModalActionExtraFieldRule,
  },
  mixins: [userRole],
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      pageloading: false,
      loading: false,
      fieldsLoading: false,
      rulesLoading: false,
      actionsLoading: false,
      deleteLoading: false,
      setForm: {
        code: '',
        title: '',
        description: '',
      },
      fieldsList: [],
      rulesList: [],
      actionsList: [],
      activeTab: 0,
      showFieldModal: false,
      fieldEdit: null,
      showRuleModal: false,
      ruleEdit: null,
      showDeleteField: false,
      deleteFieldItem: null,
      showDeleteRule: false,
      deleteRuleItem: null,
    }
  },
  computed: {
    getId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null
    },
    setId() {
      return this.getId
    },
    getTitle() {
      return this.getId
        ? 'Papildomų laukų rinkinys'
        : 'Naujas papildomų laukų rinkinys'
    },
    fieldHeaders() {
      return [
        {
          text: this.$t('Veiksmai'),
          value: 'actions',
          sortable: false,
          width: '6rem',
        },
        { text: this.$t('Raktas'), value: 'field_key' },
        { text: this.$t('Tipas'), value: 'field_type' },
        {
          text: this.$t('Leidžiama įvesti'),
          value: 'manual_input_type',
        },
        { text: this.$t('Pavadinimas (LT)'), value: 'label_lt' },
      ]
    },
    ruleHeaders() {
      return [
        {
          text: this.$t('Veiksmai'),
          value: 'actions',
          sortable: false,
          width: '6rem',
        },
        {
          text: this.$t('Laukai'),
          value: 'fields_summary',
          sortable: false,
        },
        {
          text: this.$t('Reikalinga / iš viso'),
          value: 'required_summary',
          width: '8rem',
          sortable: false,
        },
        {
          text: this.$t('Klaida (LT)'),
          value: 'error_message_lt',
          sortable: false,
        },
      ]
    },
    actionHeaders() {
      return [
        { text: this.$t('Kodas'), value: 'code' },
        { text: this.$t('PASIS kodas'), value: 'pasis_code' },
        { text: this.$t('Pavadinimas'), value: 'title' },
      ]
    },
  },
  created() {
    if (this.getId) {
      this.loadSet()
    }
  },
  beforeRouteUpdate(to, from, next) {
    next()
    if (to.params.id === from.params.id) {
      return
    }
    if (to.params.id) {
      this.loadSet()
    } else {
      this.setForm = { code: '', title: '', description: '' }
      this.fieldsList = []
      this.rulesList = []
      this.actionsList = []
      this.activeTab = 0
    }
  },
  methods: {
    unwrapSet(data) {
      return (
        data.action_extra_field_set ||
        data.actionExtraFieldSet ||
        data
      )
    },
    /**
     * Fields and rules come only from GET action-extra-field-sets/{id} (nested on the set).
     */
    applyNestedFromSet(s) {
      const fields =
        s.actionExtraFields ||
        s.action_extra_fields ||
        []
      const rules =
        s.actionExtraFieldRules ||
        s.action_extra_field_rules ||
        []
      this.fieldsList = Array.isArray(fields) ? fields.slice() : []
      this.rulesList = Array.isArray(rules) ? rules.slice() : []
    },
    loadActions() {
      if (!this.setId) {
        this.actionsList = []
        return Promise.resolve()
      }
      this.actionsLoading = true
      return this.$axios
        .get(`action-extra-field-sets/${this.setId}/actions`)
        .then((res) => {
          this.actionsList = Array.isArray(res.data?.actions)
            ? res.data.actions.slice()
            : []
        })
        .catch((err) => {
          this.$root.$emit('DATA_ERROR', err)
        })
        .finally(() => {
          this.actionsLoading = false
        })
    },
    fetchSetById() {
      return this.$axios.get(`action-extra-field-sets/${this.getId}`)
    },
    /** Reload fields + rules from show endpoint (after save/delete), without full-page overlay. */
    refreshTablesFromShowEndpoint() {
      if (!this.setId) {
        return Promise.resolve()
      }
      this.fieldsLoading = true
      this.rulesLoading = true
      return this.fetchSetById()
        .then((res) => {
          const s = this.unwrapSet(res.data)
          this.applyNestedFromSet(s)
        })
        .catch((err) => {
          this.$root.$emit('DATA_ERROR', err)
        })
        .finally(() => {
          this.fieldsLoading = false
          this.rulesLoading = false
        })
    },
    back() {
      this.$router.push({ name: 'classifierServiceExtraFields' })
    },
    formatActionExtraFieldType(value) {
      const loc = this.$i18n.locale === 'lt' ? 'lt' : 'en'
      const map = Enums.actionExtraFieldType?.[loc] || {}
      return value ? map[value] || value : '—'
    },
    formatActionExtraFieldManualInputType(item) {
      if (item.field_type === 'ntr') {
        return '—'
      }
      const v = item.manual_input_type
      if (!v) {
        return '—'
      }
      const loc = this.$i18n.locale === 'lt' ? 'lt' : 'en'
      const map = Enums.actionExtraFieldManualInputType?.[loc] || {}
      return map[v] || v
    },
    loadSet() {
      this.pageloading = true
      Promise.all([this.fetchSetById(), this.loadActions()])
        .then(([res]) => {
          const s = this.unwrapSet(res.data)
          this.setForm = {
            code: s.code || '',
            title: s.title || '',
            description: s.description || '',
          }
          this.applyNestedFromSet(s)
        })
        .catch((err) => {
          this.$root.$emit('DATA_ERROR', err)
        })
        .finally(() => {
          this.pageloading = false
        })
    },
    saveSet() {
      this.$validator.validateAll().then((ok) => {
        if (!ok) {
          return
        }
        this.loading = true
        const payload = {
          id: this.getId,
          code: this.setForm.code,
          title: this.setForm.title,
          description: this.setForm.description,
        }
        this.$axios
          .post(`action-extra-field-sets`, payload)
          .then(() => {
            this.$root.$emit('DATA_OK_CODE', '000110')
            this.back()
          })
          .catch((err) => {
            this.$root.$emit('DATA_ERROR', err)
          })
          .finally(() => {
            this.loading = false
          })
      })
    },
    openFieldModal(item) {
      this.fieldEdit = item ? { ...item } : null
      this.showFieldModal = true
    },
    openRuleModal(item) {
      this.ruleEdit = item ? { ...item } : null
      this.showRuleModal = true
    },
    deleteField(item) {
      this.deleteFieldItem = { ...item }
      this.showDeleteField = true
    },
    confirmDeleteField(item) {
      this.deleteLoading = true
      this.$axios
        .delete(`action-extra-fields/${item.id}`)
        .then(() => {
          this.$root.$emit('DATA_OK_CODE', '000111')
          this.refreshTablesFromShowEndpoint()
        })
        .catch((err) => {
          this.$root.$emit('DATA_ERROR', err)
        })
        .finally(() => {
          this.deleteLoading = false
          this.showDeleteField = false
        })
    },
    deleteRule(item) {
      this.deleteRuleItem = { ...item }
      this.showDeleteRule = true
    },
    confirmDeleteRule(item) {
      this.deleteLoading = true
      this.$axios
        .delete(`action-extra-field-rules/${item.id}`)
        .then(() => {
          this.$root.$emit('DATA_OK_CODE', '000111')
          this.refreshTablesFromShowEndpoint()
        })
        .catch((err) => {
          this.$root.$emit('DATA_ERROR', err)
        })
        .finally(() => {
          this.deleteLoading = false
          this.showDeleteRule = false
        })
    },
    formatRuleFields(rule) {
      const ids = rule.field_ids || []
      const parts = ids.map((id) => {
        const f = this.fieldsList.find((x) => Number(x.id) === Number(id))
        return f ? f.field_key : id
      })
      return parts.join(', ')
    },
  },
}
</script>
